import translate from '@translate';
import React, { FC, useEffect, useState } from 'react';
import Select, { OnChangeType, SelectValueType } from 'src/components/Select';
import { RootStoreType } from 'src/types/store';
import { useDispatch, useSelector } from 'react-redux';
import { getGlsSkPlacesFetch, getGlsSkPlaceByAddressIdFetch, clearSelectedCountry } from '../../resources/glsskPlaces/reducer';
import Grid, { GridItem } from '../Grid';

const GLS_COUNTRY_CODES = [
    'at',
    'be',
    'bg',
    'cz',
    'de',
    'dk',
    'es',
    'fi',
    'fr',
    'gr',
    'hr',
    'hu',
    'it',
    'lu',
    'nl',
    'pl',
    'pt',
    'ro',
    'si',
    'sk',
];

export interface GlsSkPlacesSelectPropsType {
    onChange: OnChangeType;
    value: SelectValueType;
    error?: boolean;
    name?: string;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    serviceCode?: string;
}

const GlsSkPlacesSelect: FC<GlsSkPlacesSelectPropsType> = (props) => {
    const dispatch = useDispatch();

    const isGlsSkPs = props.serviceCode === 'GLSSKPS';
    const [country, setCountry] = useState<string>(isGlsSkPs ? 'sk' : '');
    const [initialValueFetched, setInitialValueFetched] = useState<boolean>(false);

    const data = useSelector((state: RootStoreType) => state.glsSkPlaces.data);
    const selectedCountry = useSelector((state: RootStoreType) => state.glsSkPlaces.selectedCountry);
    const loading = useSelector((state: RootStoreType) => state.glsSkPlaces.loading);

    useEffect(() => {
        if (props.value && !initialValueFetched && !country) {
            dispatch(getGlsSkPlaceByAddressIdFetch(String(props.value)));
            setInitialValueFetched(true);
        } else if (!props.value) {
            dispatch(clearSelectedCountry());
            setInitialValueFetched(false);
        }
    }, [props.value]);

    useEffect(() => {
        if (selectedCountry) {
            setCountry(selectedCountry);
        }
    }, [selectedCountry]);

    useEffect(() => {
        if (country && !data?.[country]) {
            dispatch(getGlsSkPlacesFetch(country));
        }
    }, [country]);

    const handleChangeCountry = (value: any) => {
        if (country !== value) {
            setCountry(String(value));
            if (country) {
                props.onChange(null, true);
            }
        }
    };

    const glsCountries = GLS_COUNTRY_CODES.map((code) => ({
        label: translate(`countries.${code}`),
        value: code,
    })).sort((a, b) => a.label.localeCompare(b.label));

    const options = data[country] || [];

    const isSet = !props.value || options?.filter((option: any): boolean => option.value === props.value).length;
    const allOptions = isSet
        ? options
        : [{ label: `${translate('messages.branchUnavailable')} (${props.value})`, value: props.value }, ...options];

    return (
        <Grid>
            <GridItem cols={4}>
                <Select
                    options={isGlsSkPs ? [{ label: translate('countries.sk'), value: 'sk' }] : glsCountries}
                    value={country}
                    error={props.error}
                    onChange={handleChangeCountry}
                    name={`${props.name}country`}
                    label={translate('orderDetail.countryOfDelivery')}
                    required={props.required}
                    disabled={isGlsSkPs || props.disabled}
                    fullWidth={props.fullWidth}
                    loading={loading}
                />
            </GridItem>
            {!!country && (
                <GridItem cols={8}>
                    <Select
                        options={allOptions}
                        value={props.value}
                        error={props.error}
                        onChange={props.onChange}
                        name={props.name}
                        label={translate('orderDetail.pickupPoint')}
                        required={props.required}
                        disabled={props.disabled}
                        fullWidth={props.fullWidth}
                        loading={loading}
                        virtualized
                    />
                </GridItem>
            )}
        </Grid>
    );
};

export default GlsSkPlacesSelect;
